import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Upload, Video, Image, Music, Mic, FileText, Wand2, TrendingUp,
  DollarSign, Eye, Settings, Sparkles, Send, X, Plus, Loader2,
  Scissors, Palette, BarChart3, Zap, ChevronRight, Monitor,
  Camera, Film, PenSquare, Hash, Clock, CheckCircle
} from 'lucide-react';

const TOOLS = [
  { id: 'upload', label: 'Upload Content', icon: Upload, color: '#8B5CF6', desc: 'Upload video, audio, or images' },
  { id: 'write', label: 'Write Post', icon: PenSquare, color: '#06B6D4', desc: 'Text post, announcement, or thread' },
  { id: 'clip', label: 'Create Clip', icon: Scissors, color: '#EC4899', desc: 'Edit and create short clips' },
  { id: 'ai-caption', label: 'AI Caption Writer', icon: Wand2, color: '#F59E0B', desc: 'Generate engaging captions with AI' },
  { id: 'viral-check', label: 'Viral Score', icon: TrendingUp, color: '#10B981', desc: 'Check your content\'s viral potential' },
  { id: 'analytics', label: 'Quick Analytics', icon: BarChart3, color: '#3B82F6', desc: 'View recent performance stats' },
  { id: 'monetize', label: 'Monetize', icon: DollarSign, color: '#F97316', desc: 'Set up tips, locks, subscriptions' },
  { id: 'schedule', label: 'Schedule Post', icon: Clock, color: '#A855F7', desc: 'Plan and schedule future posts' },
];

export default function StudioModePage() {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [activeTool, setActiveTool] = useState(null);
  const [postText, setPostText] = useState('');
  const [aiCaption, setAiCaption] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [viralScore, setViralScore] = useState(null);
  const [viralLoading, setViralLoading] = useState(false);
  const [quickStats, setQuickStats] = useState(null);
  const headers = getAuthHeaders();

  useEffect(() => {
    if (activeTool === 'analytics') fetchQuickStats();
  }, [activeTool]);

  const fetchQuickStats = async () => {
    try {
      const res = await fetch(`${API}/content-creator/my-stats`, { headers });
      if (res.ok) setQuickStats(await res.json());
    } catch {}
  };

  const generateAiCaption = async () => {
    if (!postText.trim()) return;
    setAiLoading(true);
    try {
      const res = await fetch(`${API}/viral-engine/analyze`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: postText, content_type: 'text' })
      });
      if (res.ok) {
        const d = await res.json();
        setAiCaption(d.suggestions?.caption || d.analysis?.suggested_caption || 'Try something catchy and authentic!');
      }
    } catch {}
    finally { setAiLoading(false); }
  };

  const checkViralScore = async () => {
    if (!postText.trim()) return;
    setViralLoading(true);
    try {
      const res = await fetch(`${API}/viral-engine/analyze`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: postText, content_type: 'text' })
      });
      if (res.ok) {
        const d = await res.json();
        setViralScore(d.analysis || d);
      }
    } catch {}
    finally { setViralLoading(false); }
  };

  const handleToolClick = (toolId) => {
    if (toolId === 'upload') { navigate('/post/create'); return; }
    if (toolId === 'clip') { navigate('/creator-editor'); return; }
    if (toolId === 'monetize') { navigate('/creator/earnings'); return; }
    setActiveTool(toolId);
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="studio-mode-page">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto px-4 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-cyan-600 to-blue-700">
                <Monitor className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="studio-title">Studio Mode</h1>
                <p className="text-xs text-muted-foreground">Focused creation workspace</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/feed')} className="border-border text-muted-foreground">
              Exit Studio
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Tools Grid */}
          <div className="lg:col-span-1">
            <h2 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wider">Tools</h2>
            <div className="grid grid-cols-2 gap-2" data-testid="studio-tools-grid">
              {TOOLS.map(tool => {
                const Icon = tool.icon;
                const isActive = activeTool === tool.id;
                return (
                  <button key={tool.id} onClick={() => handleToolClick(tool.id)} data-testid={`studio-tool-${tool.id}`}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl transition-all text-center ${isActive ? 'bg-muted/50 ring-1 ring-border' : 'bg-muted/20 hover:bg-muted/30'}`}>
                    <div className="p-2.5 rounded-lg" style={{ backgroundColor: `${tool.color}15` }}>
                      <Icon className="w-5 h-5" style={{ color: tool.color }} />
                    </div>
                    <span className="text-xs font-medium text-foreground/80">{tool.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right: Active Tool Panel */}
          <div className="lg:col-span-2">
            {!activeTool && (
              <Card className="p-8 bg-card border-border text-center" data-testid="studio-welcome">
                <Sparkles className="w-12 h-12 text-cyan-500/40 mx-auto mb-4" />
                <h2 className="text-lg font-semibold mb-2">Welcome to Studio Mode</h2>
                <p className="text-muted-foreground text-sm max-w-md mx-auto">Select a tool from the left to start creating. Studio Mode is your focused workspace for content creation.</p>
              </Card>
            )}

            {/* Write Post Tool */}
            {activeTool === 'write' && (
              <Card className="p-5 bg-card border-border" data-testid="write-tool-panel">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold flex items-center gap-2"><PenSquare className="w-5 h-5 text-cyan-400" /> Write Post</h2>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTool(null)}><X className="w-4 h-4" /></Button>
                </div>
                <Textarea placeholder="What's on your mind? Write your post here..." value={postText} onChange={e => setPostText(e.target.value)}
                  className="min-h-32 bg-muted/30 border-border mb-3" data-testid="studio-post-textarea" />
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={() => navigate('/post/create')} className="bg-cyan-600 hover:bg-cyan-700" data-testid="studio-publish-btn">
                    <Send className="w-4 h-4 mr-1" /> Publish
                  </Button>
                  <Button size="sm" variant="outline" onClick={generateAiCaption} disabled={aiLoading || !postText} className="border-border" data-testid="studio-ai-caption-btn">
                    <Wand2 className="w-4 h-4 mr-1" /> {aiLoading ? 'Generating...' : 'AI Rewrite'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={checkViralScore} disabled={viralLoading || !postText} className="border-border" data-testid="studio-viral-check-btn">
                    <TrendingUp className="w-4 h-4 mr-1" /> {viralLoading ? 'Checking...' : 'Viral Check'}
                  </Button>
                </div>
                {aiCaption && (
                  <div className="mt-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20" data-testid="ai-caption-result">
                    <p className="text-xs text-amber-400 font-medium mb-1">AI Suggestion:</p>
                    <p className="text-sm text-foreground/80">{aiCaption}</p>
                    <Button size="sm" variant="ghost" className="text-xs mt-1 text-amber-400" onClick={() => { setPostText(aiCaption); setAiCaption(''); }}>Use this</Button>
                  </div>
                )}
                {viralScore && (
                  <div className="mt-3 p-3 rounded-lg bg-green-500/10 border border-green-500/20" data-testid="viral-score-result">
                    <p className="text-xs text-green-400 font-medium mb-1">Viral Analysis:</p>
                    <p className="text-sm text-foreground/80">{typeof viralScore === 'object' ? JSON.stringify(viralScore).slice(0, 200) : viralScore}</p>
                  </div>
                )}
              </Card>
            )}

            {/* AI Caption Tool */}
            {activeTool === 'ai-caption' && (
              <Card className="p-5 bg-card border-border" data-testid="ai-caption-tool-panel">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold flex items-center gap-2"><Wand2 className="w-5 h-5 text-amber-400" /> AI Caption Writer</h2>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTool(null)}><X className="w-4 h-4" /></Button>
                </div>
                <Textarea placeholder="Describe your content or paste your draft..." value={postText} onChange={e => setPostText(e.target.value)}
                  className="min-h-24 bg-muted/30 border-border mb-3" data-testid="caption-input" />
                <Button onClick={generateAiCaption} disabled={aiLoading || !postText} className="bg-amber-600" data-testid="generate-caption-btn">
                  {aiLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Wand2 className="w-4 h-4 mr-1" />}
                  Generate Caption
                </Button>
                {aiCaption && (
                  <div className="mt-4 p-4 rounded-lg bg-amber-500/5 border border-amber-500/10">
                    <p className="text-sm">{aiCaption}</p>
                    <Button size="sm" className="mt-2 bg-amber-600" onClick={() => navigator.clipboard.writeText(aiCaption)}>Copy</Button>
                  </div>
                )}
              </Card>
            )}

            {/* Viral Check Tool */}
            {activeTool === 'viral-check' && (
              <Card className="p-5 bg-card border-border" data-testid="viral-check-tool-panel">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold flex items-center gap-2"><TrendingUp className="w-5 h-5 text-green-400" /> Viral Score Check</h2>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTool(null)}><X className="w-4 h-4" /></Button>
                </div>
                <Textarea placeholder="Paste your content to analyze its viral potential..." value={postText} onChange={e => setPostText(e.target.value)}
                  className="min-h-24 bg-muted/30 border-border mb-3" data-testid="viral-input" />
                <Button onClick={checkViralScore} disabled={viralLoading || !postText} className="bg-green-600" data-testid="check-viral-btn">
                  {viralLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Zap className="w-4 h-4 mr-1" />}
                  Analyze
                </Button>
                {viralScore && (
                  <div className="mt-4 p-4 rounded-lg bg-green-500/5 border border-green-500/10" data-testid="viral-result">
                    <pre className="text-sm text-foreground/80 whitespace-pre-wrap">{typeof viralScore === 'object' ? JSON.stringify(viralScore, null, 2) : viralScore}</pre>
                  </div>
                )}
              </Card>
            )}

            {/* Quick Analytics */}
            {activeTool === 'analytics' && (
              <Card className="p-5 bg-card border-border" data-testid="analytics-tool-panel">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold flex items-center gap-2"><BarChart3 className="w-5 h-5 text-blue-400" /> Quick Analytics</h2>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTool(null)}><X className="w-4 h-4" /></Button>
                </div>
                {quickStats ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <StatCard label="Total Views" value={quickStats.total_views} icon={Eye} color="#3B82F6" />
                    <StatCard label="Total Likes" value={quickStats.total_likes} icon={Heart} color="#F43F5E" />
                    <StatCard label="Comments" value={quickStats.total_comments} icon={PenSquare} color="#8B5CF6" />
                    <StatCard label="Shares" value={quickStats.total_shares} icon={Send} color="#06B6D4" />
                    <StatCard label="Posts" value={quickStats.post_count} icon={FileText} color="#10B981" />
                    <StatCard label="Engagement" value={`${quickStats.engagement_rate || 0}%`} icon={TrendingUp} color="#F59E0B" />
                    {quickStats.video_count !== undefined && <StatCard label="Videos" value={quickStats.video_count} icon={Video} color="#A855F7" />}
                    {quickStats.posts_this_week !== undefined && <StatCard label="This Week" value={quickStats.posts_this_week} icon={Clock} color="#EC4899" />}
                  </div>
                ) : (
                  <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
                )}
                <Button size="sm" variant="outline" className="mt-4 border-border text-muted-foreground" onClick={() => navigate('/creator/earnings')} data-testid="full-analytics-btn">
                  <ChevronRight className="w-4 h-4 mr-1" /> Full Analytics
                </Button>
              </Card>
            )}

            {/* Schedule Tool */}
            {activeTool === 'schedule' && (
              <Card className="p-5 bg-card border-border" data-testid="schedule-tool-panel">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold flex items-center gap-2"><Clock className="w-5 h-5 text-purple-400" /> Schedule Post</h2>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTool(null)}><X className="w-4 h-4" /></Button>
                </div>
                <div className="p-6 text-center">
                  <Clock className="w-12 h-12 text-purple-400/40 mx-auto mb-3" />
                  <p className="text-muted-foreground text-sm">Post scheduling coming soon!</p>
                  <p className="text-xs text-muted-foreground/80 mt-1">For now, create your content and publish when ready.</p>
                  <Button size="sm" className="mt-4 bg-purple-600" onClick={() => navigate('/post/create')}>Create Post Now</Button>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <Card className="p-3 bg-muted/20 border-border">
      <div className="flex items-center gap-2">
        <div className="p-1.5 rounded-lg" style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-3.5 h-3.5" style={{ color }} />
        </div>
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-sm font-bold" style={{ color }}>{typeof value === 'number' ? value.toLocaleString() : value}</p>
        </div>
      </div>
    </Card>
  );
}
